import * as Phaser from 'phaser';

const FIREFLY_TEXTURE_KEY = '__painted-firefly';
const FIREFLY_SIZE = 6;
const FIREFLY_COUNT = 18;
const FIREFLY_DEPTH = 150;

export class PaintedSceneFireflies {
  private scene: Phaser.Scene;
  private emitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private active = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.ensureTexture();
  }

  activate(): void {
    if (this.active) return;
    this.active = true;

    const w = this.scene.scale.width;
    const h = this.scene.scale.height;

    this.emitter = this.scene.add.particles(0, 0, FIREFLY_TEXTURE_KEY, {
      x: { min: w * 0.1, max: w * 0.9 },
      y: { min: h * 0.3, max: h * 0.85 },
      quantity: 1,
      frequency: 800,
      lifespan: { min: 2500, max: 5000 },
      maxParticles: FIREFLY_COUNT,
      alpha: { start: 0, end: 0, ease: 'Sine.easeInOut' },
      scale: { min: 0.4, max: 1.0 },
      speedX: { min: -12, max: 12 },
      speedY: { min: -8, max: 8 },
      tint: 0xffeeaa,
      blendMode: Phaser.BlendModes.ADD,
      emitting: true,
    });
    this.emitter.setDepth(FIREFLY_DEPTH);

    this.emitter.setParticleAlpha((p: Phaser.GameObjects.Particles.Particle) => {
      const life = p.lifeT; // 0→1 over lifetime
      // Bell curve: fade in 0→0.3, hold 0.3→0.7, fade out 0.7→1.0
      if (life < 0.3) return (life / 0.3) * 0.7;
      if (life > 0.7) return ((1 - life) / 0.3) * 0.7;
      return 0.7;
    });
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    if (this.emitter) {
      this.emitter.stop();
      this.scene.time.delayedCall(5000, () => {
        this.emitter?.destroy();
        this.emitter = null;
      });
    }
  }

  isActive(): boolean {
    return this.active;
  }

  destroy(): void {
    this.active = false;
    this.emitter?.destroy();
    this.emitter = null;
  }

  private ensureTexture(): void {
    if (this.scene.textures.exists(FIREFLY_TEXTURE_KEY)) return;
    const s = FIREFLY_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = s;
    canvas.height = s;
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    gradient.addColorStop(0, 'rgba(255, 238, 170, 1)');
    gradient.addColorStop(0.5, 'rgba(255, 238, 170, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 238, 170, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, s, s);
    this.scene.textures.addCanvas(FIREFLY_TEXTURE_KEY, canvas);
  }
}
